// Visual presets for the marketing site. Each preset combines a
// colour palette, a font pairing and a corner radius. The CSS for
// each lives in app/globals.css under `.<key> { … }`. The theme
// switcher (components/site/theme-switcher.tsx) renders this list.
//
// `mode` drives Tailwind's `dark:` variants, the ThemeSync component
// in theme-provider.tsx adds/removes the `dark` class on <html> based
// on the active theme's mode.
export type ThemeMode = 'light' | 'dark'

export type ThemeDef = {
  key: string
  label: string
  description: string
  mode: ThemeMode
  // Two HSL strings used to render the preview swatch in the picker.
  swatchBg: string
  swatchFg: string
  swatchAccent: string
  // Short label of the font family used for headings (for the picker).
  fontLabel: string
}

export const defaultTheme = 'idukki'

export const themes: ThemeDef[] = [
  {
    key: 'idukki',
    label: 'Idukki',
    description: 'The canonical brand: cool paper, deep navy ink, cobalt action colour, Plus Jakarta Sans. The single theme the marketing site ships (forced live).',
    mode: 'light',
    swatchBg: 'hsl(228 30% 97%)',
    swatchFg: 'hsl(222 45% 10%)',
    swatchAccent: 'hsl(216 90% 45%)',
    fontLabel: 'Plus Jakarta Sans',
  },
  {
    key: 'editorial-cream',
    label: 'Editorial Cream',
    description: 'Base token layer (:root). Cobalt-mapped neutral light surface; the fallback values other themes inherit.',
    mode: 'light',
    swatchBg: 'hsl(228 30% 97%)',
    swatchFg: 'hsl(222 45% 10%)',
    swatchAccent: 'hsl(216 90% 45%)',
    fontLabel: 'Plus Jakarta Sans',
  },
  {
    key: 'editorial-ink',
    label: 'Editorial Ink',
    description: 'Dark counterpart (maps onto the .dark class). Kept for the retired theme switcher and dark-mode parity.',
    mode: 'dark',
    swatchBg: 'hsl(224 24% 7%)',
    swatchFg: 'hsl(36 30% 95%)',
    swatchAccent: 'hsl(216 90% 55%)',
    fontLabel: 'Plus Jakarta Sans',
  },
  {
    key: 'slate-pro',
    label: 'Slate Pro',
    description: 'Cobalt design-system light surface (#0B5CD8 accent), Plus Jakarta Sans. Retained CSS block.',
    mode: 'light',
    swatchBg: 'hsl(228 30% 97%)',
    swatchFg: 'hsl(222 45% 10%)',
    swatchAccent: 'hsl(216 90% 45%)',
    fontLabel: 'Plus Jakarta Sans',
  },
  {
    key: 'slate-pro-dark',
    label: 'Slate Pro Dark',
    description: 'The cobalt system after dark: gunmetal slate, lifted cobalt accent, Plus Jakarta Sans. Retained CSS block.',
    mode: 'dark',
    swatchBg: 'hsl(220 24% 9%)',
    swatchFg: 'hsl(220 16% 93%)',
    swatchAccent: 'hsl(232 80% 66%)',
    fontLabel: 'Plus Jakarta Sans',
  },
]

export const themeKeys = themes.map((t) => t.key)

export function isDarkTheme(key: string): boolean {
  return themes.find((t) => t.key === key)?.mode === 'dark'
}
